import { HttpStatusCode } from 'axios';

/**
 * Authentication error context constants
 * @public
 */
export const AUTH_ERROR_TYPES = {
  FORGOT_PASSWORD: 'forgot-password',
  RESET_PASSWORD: 'reset-password',
  GENERAL: 'general',
} as const;

/**
 * Supported error handling contexts for authentication flows
 * @public
 */
export type AuthErrorType =
  (typeof AUTH_ERROR_TYPES)[keyof typeof AUTH_ERROR_TYPES];

/**
 * Base HTTP status codes to translation key mappings
 * @internal
 */
export const BASE_HTTP_ERRORS: Partial<Record<HttpStatusCode, string>> = {
  [HttpStatusCode.BadRequest]: 'errorBadRequest',
  [HttpStatusCode.Unauthorized]: 'errorUnauthorized',
  [HttpStatusCode.Forbidden]: 'errorForbidden',
  [HttpStatusCode.NotFound]: 'errorNotFound',
  [HttpStatusCode.TooManyRequests]: 'errorTooManyRequests',
  [HttpStatusCode.InternalServerError]: 'errorServerError',
  [HttpStatusCode.BadGateway]: 'errorServiceUnavailable',
  [HttpStatusCode.ServiceUnavailable]: 'errorServiceUnavailable',
  [HttpStatusCode.GatewayTimeout]: 'errorServiceUnavailable',
};

/**
 * Context-specific error overrides for different authentication flows
 * @internal
 */
export const CONTEXT_OVERRIDES: Record<
  AuthErrorType,
  Partial<Record<HttpStatusCode, string>>
> = {
  [AUTH_ERROR_TYPES.FORGOT_PASSWORD]: {
    [HttpStatusCode.BadRequest]: 'errorInvalidEmail',
    [HttpStatusCode.NotFound]: 'errorUserNotFound',
  },
  [AUTH_ERROR_TYPES.RESET_PASSWORD]: {
    [HttpStatusCode.BadRequest]: 'errorInvalidCode',
    [HttpStatusCode.NotFound]: 'errorNotFound',
    [HttpStatusCode.Gone]: 'errorExpired',
  },
  [AUTH_ERROR_TYPES.GENERAL]: {},
};

/**
 * Return type for the useAuthErrorHandler hook
 * @public
 */
export interface UseAuthErrorHandlerReturn {
  /**
   * Gets localized error message for a given error object
   * @param error - Error object from API call or network request
   * @returns Localized error message string
   */
  getErrorMessage: (error: any) => string;
}
